import { TInitialProductDataDB, TSeatCoverDataDB } from './db/index';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { modelStrings } from './constants';
import { IProductData } from '@/utils';

import { parsePhoneNumber } from 'libphonenumber-js';
import { IProductData, TQueryParams } from '@/utils';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function slugToDisplayName(model: string) {
  return modelStrings[model] ?? model;
}

export function stringToSlug(str: string) {
  // Replace spaces with + and remove special characters, then convert to lower case
  const slug = str
    ?.trim() // Remove whitespace from both ends of a string
    .toLowerCase() // Convert the string to lowercase letters
    .replace(/[^a-z0-9 ]/g, '') // Remove all non-word chars except spaces
    .replace(/\s+/g, '+'); // Replace spaces with +

  return slug;
}

//TODO - slowly migrate from slugify to compareRawStrings for string comparison (not for URL generation)
export const slugify = (str: string) =>
  str
    ?.toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-') // Replace all non-alphanumeric characters with a dash
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing dashes

export const deslugify = (slug: string) => {
  if (typeof slug !== 'string') return slug;

  // Handle specific cases
  if (slug.toLowerCase() === 'bmw') return 'BMW';
  if (slug.toLowerCase() === '1-series-hatchback') return '1-Series Hatchback';
  if (slug.toLowerCase() === '2-series') return '2-Series';
  if (slug.toLowerCase() === '3-series') return '3-Series';
  if (slug.toLowerCase() === '4-series') return '4-Series';
  if (slug.toLowerCase() === '5-series') return '5-Series';
  if (slug.toLowerCase() === '6-series') return '6-Series';
  if (slug.toLowerCase() === '7-series') return '7-Series';
  if (slug.toLowerCase() === 'f-150') return 'F-150';
  if (slug.toLowerCase() === 'suv') return 'SUV';
  if (slug.toLowerCase() === 'suv covers') return 'SUV Covers';
  if (slug.toLowerCase() === 'wrx') return 'WRX';
  if (slug.toLowerCase() === 'wrx wrx sti') return 'WRX, WRX STI';
  if (slug.toLowerCase() === 'sti') return 'STI';
  if (slug.toLowerCase() === 'ram') return 'RAM';
  if (slug.toLowerCase() === 'mdx') return 'MDX';
  if (slug.toLowerCase() === 'rdx') return 'RDX';
  if (slug.toLowerCase() === 'ilx') return 'ILX';
  if (slug.toLowerCase() === 'tl') return 'TL';
  if (slug.toLowerCase() === 'tlx') return 'TLX';

  // Check if the format is like a date range (e.g., 2006-2016)
  const dateRangePattern = /^\d{4}-\d{4}$/;
  if (dateRangePattern.test(slug)) return slug;

  // General case
  return slug
    .replace(/-/g, ' ')
    .trim()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export function getNewestModel(array: TInitialProductDataDB[]) {
  return array.sort((a, b) => {
    const getLastYear = (item: TInitialProductDataDB) => {
      if (!item?.year_generation) return 0;
      const years = item?.year_generation.split('-');
      return parseInt(years[years.length - 1]);
    };

    const lastYearA = getLastYear(a);
    const lastYearB = getLastYear(b);

    return lastYearB - lastYearA;
  });
}

export function getUniqueYearGenerations(array: TInitialProductDataDB[]) {
  const unique = new Set();

  array.forEach((item) => {
    if (item.year_generation !== null) {
      unique.add(item.year_generation);
    }
  });

  return Array.from(unique);
}

//TODO - slowly migrate from slugify to compareRawStrings for string comparison (not for URL generation)
//TODO - maybe a library of function to more reliably compare strings
export function compareRawStrings(str1: string | null | undefined, str2: string | null) {
  if (!str1 || !str2) return false;

  let processedStr1 = String(str1).toLowerCase();
  let processedStr2 = String(str2).toLowerCase();

  processedStr1 = processedStr1.replace(/[^a-z0-9]/g, '');
  processedStr2 = processedStr2.replace(/[^a-z0-9]/g, '');

  return processedStr1 === processedStr2;
}

export function groupProductsBy(
  attribute: keyof TInitialProductDataDB,
  array: TInitialProductDataDB[]
) {
  const groups = array.reduce(
    (accumulator, currentValue) => {
      const groupKey = String(currentValue[attribute]);
      if (!accumulator[groupKey]) {
        accumulator[groupKey] = [];
      }
      accumulator[groupKey].push(currentValue);
      return accumulator;
    },
    {} as Record<string, TInitialProductDataDB[]>
  );

  return Object.keys(groups);
}

export const generateProductsLeft = (
  selectedProduct: TInitialProductDataDB | null | undefined
): number => {
  let productAmount = 0;
  if (selectedProduct && selectedProduct.sku) {
    for (let i = 0; i < selectedProduct.sku.length; i++) {
      const char = selectedProduct.sku[i];
      productAmount += char.charCodeAt(0) * i;
    }
    productAmount = Math.log(productAmount);
    const productString = productAmount.toString();
    for (let i = 5; i < productString.length; i++) {
      const num = parseInt(productString[i]);
      if (num > 2 && num < 7) {
        productAmount = num;
        break;
      }
    }
  }
  return productAmount;
};

export const generateNumberFromCarBuild = (
  type: string,
  displayId: string,
  make: string,
  model: string,
  yearGeneration: string
): number => {
  const combinedString = `${type}${displayId}${make}${model}${yearGeneration}`;

  // Simple hash function to convert SKU to a numeric value
  const hash = Array.from(combinedString).reduce((acc, char) => {
    return acc + char.charCodeAt(0);
  }, 0);
  // Normalize the hash value to a range between 0 and 1
  const normalizedHash = (hash % 1000) / 1000;

  // Scale the normalized value to the range [4.6, 4.8]
  const scaledValue = 4.5 + normalizedHash * 0.3;
  return scaledValue;
};

// export function getColorOptions(data: Model[], selectedModel: { year_generation: number }): string[] {
//   return data
//     .filter(
//       (model): model is Model & { feature: string[] } =>
//         model.year_generation === selectedModel.year_generation &&
//         model.feature?.includes('category-images-new')
//     )
//     .flatMap((model) => model.feature)
//     .filter(Boolean) as string[];
// }

export type CoverType = 'premium-plus' | 'premium' | 'standard-pro' | 'standard';
export type ProductType = 'car-covers' | 'suv-covers' | 'truck-covers';

export type CoverOption = { coverType: CoverType };
export type ProductOption = { productType: ProductType };

export const coverOptions: CoverOption[] = [
  { coverType: 'premium-plus' },
  { coverType: 'premium' },
  { coverType: 'standard-pro' },
  { coverType: 'standard' },
];

export const productOptions: ProductOption[] = [
  { productType: 'car-covers' },
  { productType: 'suv-covers' },
  { productType: 'truck-covers' },
];

export function combineOptions(
  coverOptions: CoverOption[],
  productOptions: ProductOption[]
): { coverType: CoverType; productType: ProductType }[] {
  const combinedOptions: { coverType: CoverType; productType: ProductType }[] = [];

  coverOptions.forEach((coverOption) => {
    productOptions.forEach((productOption) => {
      combinedOptions.push({ ...productOption, ...coverOption });
    });
  });

  return combinedOptions;
}

// Example SKU: CL-SC-10-F-11-BK-1TO-1136
// Example SKU (old): CL-SC-10-FB-100-GR-1TO-3044
// Example Full Set SKU 5/29/24: CL-SC-10-F-10-B-32-BE-1TO-20017
export function detectFOrFB(sku: string) {
  const parts = sku.split('-');
  if (parts[1] === 'SC') {
    if (parts[5] === 'B') {
      return 'Full';
    } else if (parts[3] === 'F') {
      return 'Front';
    }
  }
  return 'Unknown';
}

/*
  CN do not have mirrors, 
  CS and CP include mirrors. 
  FOMU12, CHCM11, and CHCV11 do not have mirror pockets, 
  while all other custom sizes come with mirror pockets.
*/
export function hasMirrors(sku: string) {
  const skuSubstringsNoMirror = ['cn', 'fomu12', 'chcm11', 'chcv11'];
  const lowerStr = sku.toLowerCase();

  if (skuSubstringsNoMirror.some((substring) => lowerStr.includes(substring))) {
    return false;
  }

  return true;
}

export function isFullSet(displaySet: string): string {
  const normalizedDisplaySet = displaySet?.toLowerCase();
  if (normalizedDisplaySet === 'front seats' || normalizedDisplaySet === 'front seat') {
    return 'front';
  } else if (
    normalizedDisplaySet === 'full set' ||
    normalizedDisplaySet === 'full-set' ||
    normalizedDisplaySet === 'fullset' ||
    normalizedDisplaySet === 'full seat set'
  ) {
    return 'full';
  } else if (normalizedDisplaySet === 'rear seat' || normalizedDisplaySet === 'rear seats') {
    return 'rear';
  } else if (
    normalizedDisplaySet === 'third row' ||
    normalizedDisplaySet === 'thrid row' ||
    normalizedDisplaySet === 'third rows'
  ) {
    return 'third';
  }
  return 'unknown';
}

export const determineTypeString = (type: string) => {
  const typeOptions = ['Car Covers', 'SUV Covers', 'Truck Covers'];
  return type === 'car-covers'
    ? typeOptions[0]
    : type === 'suv-covers'
      ? typeOptions[1]
      : type === 'truck-covers'
        ? typeOptions[2]
        : type;
};

export const determineCoverType = (type: string) => {
  let coverType;
  switch (type) {
    case 'premium-plus':
      coverType = 'Premium Plus';
      break;
    case 'premium':
      coverType = 'Premium';
      break;
    case 'standard':
      coverType = 'Standard';
      break;
    case 'standard-pro':
      coverType = 'Standard Pro';
      break;
    default:
      coverType = 'Premium Plus';
      break;
  }
  return coverType;
};

export const determineShortReviewCount = (total_reviews: number) => {
  switch (true) {
    case total_reviews >= 100 && total_reviews <= 199:
      return '100+';
    case total_reviews >= 200 && total_reviews <= 299:
      return '200+';
    case total_reviews >= 300 && total_reviews <= 399:
      return '300+';
    case total_reviews >= 400 && total_reviews <= 499:
      return '400+';
    case total_reviews >= 500 && total_reviews <= 599:
      return '500+';
    case total_reviews >= 600 && total_reviews <= 699:
      return '600+';
    case total_reviews >= 700 && total_reviews <= 799:
      return '700+';
    case total_reviews >= 800 && total_reviews <= 899:
      return '800+';
    case total_reviews >= 900 && total_reviews <= 999:
      return '900+';
    default:
      return total_reviews;
  }
};

export const formatToE164 = (num: string) => {
  if (!num) {
    return '';
  }

  const formattedPhone = parsePhoneNumber(num, 'US')?.format('E.164');
  // console.log('[FORMAT TO E.164 FUNCTION: ]', { formattedPhone });
  return formattedPhone;
};

/**
 * If there are no products to show, it will default to black, preorder, 16 weeks ahead, 40/60 for preorder discount (Front/Full)
 * @param products
 * @returns
 */
export function updateProductPreorderDetailsWithDynamicLogic(
  products: TSeatCoverDataDB[]
): TSeatCoverDataDB[] {
  const sixteenWeeksFromToday = new Date();
  sixteenWeeksFromToday.setDate(sixteenWeeksFromToday.getDate() + 16 * 7);
  const preorderDate = sixteenWeeksFromToday.toISOString().split('T')[0];

  return products
    .filter((product) => product?.display_color?.toLowerCase() === 'black')
    .map((product) => ({
      ...product,
      preorder: true,
      preorder_date: preorderDate,
      preorder_discount: product?.display_set?.toLowerCase() === 'front seats' ? 40 : 60,
    }))
    .sort((a, b) => {
      if (a?.display_set?.toLowerCase() === 'front seats') return -1;
      if (b?.display_set?.toLowerCase() === 'front seats') return 1;
      return 0;
    });
}

export const getLatestYear = (data: { year_generation?: string } | null): number | null => {
  // Extract the year_generation field
  const yearGeneration = data?.year_generation;

  if (!yearGeneration || typeof yearGeneration !== 'string') {
    return null; // Return null if no valid input
  }

  // Check if the input is a range (e.g., "2011-2025")
  if (yearGeneration.includes('-')) {
    const [, endYear] = yearGeneration.split('-').map(Number);

    // Ensure endYear is a valid number
    return isNaN(endYear) ? null : endYear;
  }

  // Handle single year input (e.g., "2025")
  const singleYear = Number(yearGeneration);
  return isNaN(singleYear) ? null : singleYear;
};

export const capitalizeModelDetail = (modelDetail: string): string => {
  return modelDetail
    .split(' ')
    .map((word) => {
      // Match patterns like `1500trx`, `1500hd`, etc.
      const match = word.match(/^(\d+)(trx|hd|classic)$/i);
      if (match) {
        const [, num, detail] = match;
        if (detail.toLowerCase() === 'trx') {
          return `${num}TRX`;
        } else if (detail.toLowerCase() === 'hd') {
          return `${num}HD`;
        } else if (detail.toLowerCase() === 'classic') {
          return `${num}Classic`;
        }
      }

      // Handle standalone words like `Trx`, `HD`, `Classic`
      if (word.toLowerCase() === 'trx') return 'TRX';
      if (word.toLowerCase() === 'hd') return 'HD';
      if (word.toLowerCase() === 'classic') return 'Classic';

      return word; // Leave other words/numbers unchanged
    })
    .join(' ');
};

export const getCountryName = (isoCode: string, locale = 'en') => {
  return new Intl.DisplayNames([locale], { type: 'region' }).of(isoCode);
};

export const extractSequenceNumber = (
  orderNumberWithSequence: string,
  originalOrderNumber: string
) => {
  // If we have the original order number for comparison
  if (originalOrderNumber) {
    // If they're the same, it's not a split order
    if (orderNumberWithSequence === originalOrderNumber) {
      return null; // Not a split order
    }

    // If the split order starts with the original order number plus a dash
    if (orderNumberWithSequence.startsWith(originalOrderNumber + '-')) {
      const suffix = orderNumberWithSequence.substring(originalOrderNumber.length + 1);
      if (/^\d+$/.test(suffix)) {
        return parseInt(suffix, 10);
      }
    }
  } else {
    // Without an original order number for reference, we need to be more careful
    // Check if the last segment matches the pattern of a split order suffix
    const parts = orderNumberWithSequence.split('-');

    // If there are at least two parts (e.g., "CL-TEST-PRE-250401-SE-1453-2")
    if (parts.length >= 2) {
      const lastPart = parts[parts.length - 1];

      // Check if the last part is a small number (typically 1-9 for split orders)
      if (/^\d+$/.test(lastPart) && parseInt(lastPart, 10) < 100) {
        // This is likely a split order suffix
        return parseInt(lastPart, 10);
      }
    }
  }

  // If not a split order or can't determine
  return null;
};

export const isCabSize = (name?: string) => {
  if (!name) return false;
  const cabKeywords = ['cab']; // you can expand this list if needed
  return cabKeywords.some((keyword) => name.toLowerCase().includes(keyword));
};

export const cleanCabSizeName = (name?: string) => {
  if (!name) return '';
  return name.replace(/size/gi, '').trim();
};

export const isBedSize = (name?: string) => {
  if (!name) return false;
  return name.toLowerCase().includes('bed');
};

export const cleanBedSizeName = (name?: string) => {
  if (!name) return '';
  return 'Bed'; // Or return name.replace(/bed size/gi, 'bed').trim() if you want partial cleanup
};

export const removeMirrorOrSeatText = (value?: string) => {
  if (!value) return '';
  return value.replace(/mirror|seat/gi, '').trim();
};

export const changeSeatStyleToSeatSet = (value?: string) => {
  if (!value) return '';
  return value.replace(/style/gi, 'Type');
};

/**
 * Function to help print out selected fields from an object. Originally used for selected product
 * @param obj
 * @param fields
 * @returns
 */
export function formatSelectedFields<T extends Record<string, any>>(
  obj: T,
  fields: (keyof T)[]
): string {
  const selected = fields.reduce((acc, key) => {
    if (key in obj) acc[key] = obj[key];
    return acc;
  }, {} as Partial<T>);

  return JSON.stringify(selected, null, 2);
}

export function filterProductsBySearchParams<T extends TInitialProductDataDB | IProductData>(
  products: T[],
  searchParams: TQueryParams
): T[] {
  let filteredData = [...products];
  // console.log('Initial FilteredData:', filteredData.length);  // Leaving for when debugging filtering on page.tsx level
  const hasModelDetailOptions = products.some(
    (product) => product.model_detail && product.model_detail.trim() !== ''
  );

  // Apply all search param filters
  if (searchParams?.color) {
    filteredData = filteredData.filter((model) => {
      return compareRawStrings(model.display_color, deslugify(searchParams.color as string));
    });
    // console.log('Colors FilteredData:', filteredData.length); // Leaving for when debugging filtering on page.tsx level
  }

  if (searchParams?.year) {
    filteredData = filteredData.filter((model) =>
      yearMatches(model.year_generation, searchParams?.year)
    );
    // console.log('Years FilteredData:', filteredData.length);  // Leaving for when debugging filtering on page.tsx level
  }

  if (searchParams?.bodyTrim) {
    filteredData = filteredData.filter((model) =>
      compareRawStrings(model.submodel1, searchParams.bodyTrim as string)
    );
    // console.log('bodyTrim FilteredData:', filteredData.length);  // Leaving for when debugging filtering on page.tsx level
  }

  if (searchParams?.body) {
    filteredData = filteredData.filter((model) =>
      compareRawStrings(model.submodel1, searchParams.body as string)
    );
    // console.log('body FilteredData:', filteredData.length);  // Leaving for when debugging filtering on page.tsx level
  }

  // Support both submodel (new) and modelDetail (backwards compatibility)
  const modelDetailParam = searchParams?.submodel || searchParams?.modelDetail;
  if (hasModelDetailOptions && modelDetailParam) {
    filteredData = filteredData.filter((model) =>
      compareModelDetails(model.model_detail, modelDetailParam as string)
    );
  }

  if (searchParams?.cab) {
    filteredData = filteredData.filter((model) => {
      // console.log('Submodel1:', model.submodel1);
      // console.log('searchParams:', searchParams.cab);
      return compareCabSizes(model.submodel1, searchParams.cab as string);
    });
    // console.log('cab FilteredData:', filteredData.length);  // Leaving for when debugging filtering on page.tsx level
  }

  // TODO: Going to need some parsing for this probably
  if (searchParams?.bed) {
    filteredData = filteredData.filter((model) =>
      compareBedSizes(model.submodel2 || '', searchParams.bed as string)
    );
    // console.log('bed FilteredData:', filteredData.length);  // Leaving for when debugging filtering on page.tsx level
  }

  if (searchParams?.mirror) {
    filteredData = filteredData.filter((model) => {
      // console.log('Submodel3:', model.submodel3);
      // console.log('searchParams:', searchParams.mirror);
      return compareMirror(model.submodel3, searchParams.mirror as string);
    });
    // console.log('mirror FilteredData:', filteredData.length);  // Leaving for when debugging filtering on page.tsx level
  }

  // Seat cover specific filters
  if (searchParams?.set) {
    filteredData = filteredData.filter((model) => {
      return compareDisplaySet(model.display_set || '', deslugify(searchParams.set as string));
    });
    // console.log('set FilteredData:', filteredData.length);  // Leaving for when debugging filtering on page.tsx level
  }

  if (searchParams?.style) {
    filteredData = filteredData.filter((model) => {
      return compareRawStrings(model.submodel2, deslugify(searchParams.style as string));
    });
    // console.log('style FilteredData:', filteredData.length);  // Leaving for when debugging filtering on page.tsx level
  }

  if (searchParams?.fitment) {
    filteredData = filteredData.filter((model) => {
      if (searchParams?.cab) {
        return compareRawStrings(slugify(model.submodel2), searchParams.fitment as string);
      }
      return compareRawStrings(slugify(model.submodel1), searchParams.fitment as string);
    });
    // console.log('fitment FilteredData:', filteredData.length);  // Leaving for when debugging filtering on page.tsx level
  }
  if (searchParams?.seatType) {
    filteredData = filteredData.filter((model) => {
      return compareSeatTypeOptions(
        model as TSeatCoverDataDB,
        'Seat Type',
        searchParams.seatType as string
      );
    });
    // console.log('fitment FilteredData:', filteredData.length);  // Leaving for when debugging filtering on page.tsx level
  }

  // Seat type filters for multi-select
  if (searchParams?.firstRow) {
    filteredData = filteredData.filter((model) => {
      return compareSeatTypeOptions(
        model as TSeatCoverDataDB,
        'Front Seat',
        searchParams.firstRow as string
      );
    });
  }

  if (searchParams?.secondRow) {
    filteredData = filteredData.filter((model) => {
      return compareSeatTypeOptions(
        model as TSeatCoverDataDB,
        'Rear Seat',
        searchParams.secondRow as string
      );
    });
  }

  if (searchParams?.thirdRow) {
    filteredData = filteredData.filter((model) => {
      return compareSeatTypeOptions(
        model as TSeatCoverDataDB,
        'Third Row',
        searchParams.thirdRow as string
      );
    });
  }

  return filteredData;
}

/**
 * Find product index based on search parameters, fallback to year-only, then index 0
 */
export function getSelectedProductIndex(products: IProductData[], searchParams?: any): number {
  if (!searchParams || Object.keys(searchParams).length === 0) {
    // For seat covers, prioritize by seat type preference: Front Seat > Rear Seat > Full Set > others
    const seatTypePriority = ['Front Seat', 'Rear Seat', 'Full Set'];

    for (const seatType of seatTypePriority) {
      const index = products.findIndex((product) => product.display_set === seatType);
      if (index !== -1) {
        return index;
      }
    }

    // Fallback to first product (works for car covers and products without display_set)
    return 0;
  }

  const filteredProducts = filterProductsBySearchParams(products, searchParams);

  // If we have filtered results, find the index of the first match in the original array
  if (filteredProducts.length > 0) {
    const firstMatch = filteredProducts[0];
    const originalIndex = products.findIndex(
      (product) =>
        product.id === firstMatch.id ||
        (product.sku === firstMatch.sku && product.display_color === firstMatch.display_color)
    );
    return originalIndex !== -1 ? originalIndex : 0;
  }

  // Fallback to year-only if no matches found
  if (searchParams.year) {
    const yearOnlyMatch = products.findIndex((product) =>
      yearMatches(product.year_generation, searchParams.year as string)
    );
    if (yearOnlyMatch !== -1) {
      return yearOnlyMatch;
    }
  }

  // Final fallback
  return 0;
}

/**
 * Check if a year matches a year generation range
 */
function yearMatches(yearGeneration: string, targetYear: string): boolean {
  if (!yearGeneration || !targetYear) return false;

  const year = Number(targetYear);
  if (isNaN(year)) return false;

  if (yearGeneration.includes('-')) {
    const [start, end] = yearGeneration.split('-').map(Number);
    return year >= start && year <= end;
  }

  return Number(yearGeneration) === year;
}

/**
 * Convert bed size from URL format to comparison format
 * @param bedParam - URL parameter like "6-5", "long", "short"
 * @returns formatted bed size for comparison
 */
export function convertBedParamToSize(bedParam: string): string {
  if (!bedParam) return bedParam;

  // Handle numeric format: "6-5" -> "6.5"
  if (bedParam.includes('-')) {
    const [feet, inches] = bedParam.split('-');
    return `${feet}.${inches}`;
  }

  // Handle named formats: "long" -> "long"
  return bedParam.toLowerCase();
}

/**
 * Extract bed identifier from full bed description
 * @param bedDescription - Full description like "6.5 ft. Regular Bed", "Long Bed", "Short Bed"
 * @returns extracted identifier for comparison
 */
export function extractBedIdentifier(bedDescription: string): string {
  if (!bedDescription) return '';

  // Check if there are numbers - if so, extract them
  const numericMatch = bedDescription.match(/(\d+\.\d+)/);
  if (numericMatch) {
    return numericMatch[1]; // Returns "6.5", "5.1", etc.
  }

  // No numbers found - remove "bed" and use the remaining word
  return bedDescription
    .toLowerCase()
    .replace(/\s*bed\s*/gi, '') // Remove "bed" with any surrounding spaces
    .trim()
    .split(' ')[0]; // Take first word in case there are multiple
}

/**
 * Compare bed sizes accounting for URL format vs database format
 * @param productBedSize - Product bed size from database
 * @param bedParam - URL parameter
 * @returns true if they match
 */
export function compareBedSizes(productBedSize: string, bedParam: string): boolean {
  if (!bedParam || !productBedSize) return false;

  const paramValue = convertBedParamToSize(bedParam); // "6-5" -> "6.5" or "long" -> "long"
  const productIdentifier = extractBedIdentifier(productBedSize); // Extract from DB value

  // console.log('compareBedSizes:', {
  //   productBedSize,
  //   bedParam,
  //   paramValue,
  //   productIdentifier,
  // });

  return paramValue === productIdentifier;
}

/**
 * Compare cab sizes accounting for URL format vs database format
 * @param cabParam - URL parameter like "crew" or "regular"
 * @param productCabSize - Product cab size like "Crew Cab" or "Regular Cab"
 * @returns true if they match
 */
export function compareCabSizes(productCabSize: string, cabParam: string): boolean {
  if (!cabParam || !productCabSize) return false;

  // Remove "cab" from the product cab size and compare
  const productCabType = productCabSize
    .toLowerCase()
    .replace(/\s*cab\s*/i, '')
    .trim();
  const paramCabType = cabParam.toLowerCase().trim();
  // console.log('CompareCabSize:', { productCabType, paramCabType });

  return productCabType === paramCabType;
}

/**
 * Compare mirror types accounting for URL format vs database format
 * @param productMirrorType - Product mirror type like "Power Mirror" or "Manual Mirror"
 * @param mirrorParam - URL parameter like "power" or "manual"
 * @returns true if they match
 */
export function compareMirror(productMirrorType: string, mirrorParam: string): boolean {
  if (!productMirrorType || !mirrorParam) return false;

  // Strip "mirror" from the product mirror type and compare
  const cleanProductMirror = productMirrorType
    .toLowerCase()
    .replace(/\s*mirror\s*/i, '')
    .trim();
  const cleanMirrorParam = mirrorParam.toLowerCase().trim();

  // console.log('CompareMirror:', {
  //   original: { productMirrorType, mirrorParam },
  //   cleaned: { cleanProductMirror, cleanMirrorParam },
  //   match: cleanProductMirror === cleanMirrorParam,
  // });

  return cleanProductMirror === cleanMirrorParam;
}

/**
 * Compare model details accounting for URL format vs database format
 * @param productModelDetail - Product model detail from database like "1500 Classic", "1500 TRX", "1500"
 * @param submodelParam - URL parameter like "1500-classic", "1500-trx", "1500"
 * @returns true if they match
 */
export function compareModelDetails(productModelDetail: string, submodelParam: string): boolean {
  if (!productModelDetail || !submodelParam) return false;

  // Convert URL format to display format
  // "1500-classic" -> "1500 Classic", "1500-trx" -> "1500 TRX"
  const convertedParam = submodelParam
    .toLowerCase()
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

  // Clean both strings for comparison
  const cleanProductDetail = productModelDetail.toLowerCase().replace(/[^a-z0-9]/g, '');
  const cleanParamDetail = convertedParam.toLowerCase().replace(/[^a-z0-9]/g, '');

  // console.log('compareModelDetails:', {
  //   productModelDetail,
  //   submodelParam,
  //   convertedParam,
  //   cleanProductDetail,
  //   cleanParamDetail,
  //   match: cleanProductDetail === cleanParamDetail,
  // });

  return cleanProductDetail === cleanParamDetail;
}

export function compareDisplaySet(productDisplaySet: string, setParam: string): boolean {
  if (!productDisplaySet || !setParam) return false;

  // Clean and normalize the product display set
  // Convert "Front Seat" -> "front", "Rear Seat" -> "rear", "Full Set" -> "full"
  const cleanProductSet = productDisplaySet
    .toLowerCase()
    .replace(/\s*seat\s*/gi, '') // Remove "seat" with any surrounding spaces
    .replace(/\s*set\s*/gi, '') // Remove "set" with any surrounding spaces
    .trim();

  const cleanSetParam = setParam.toLowerCase().trim();

  // console.log('CompareDisplaySet:', {
  //   original: { productDisplaySet, setParam },
  //   cleaned: { cleanProductSet, cleanSetParam },
  //   match: cleanProductSet === cleanSetParam,
  // });

  return cleanProductSet === cleanSetParam;
}

/**
 * Compare seat type options for multi-select seat covers
 * @param product - Seat cover product from database
 * @param expectedSeatTypeOption - Expected seat type option like "Front Seat", "Rear Seat", "Third Row"
 * @param urlParam - URL parameter like "front-bucket-seat", "front-40-20-40-bench-seat"
 * @returns true if they match
 */
export function compareSeatTypeOptions(
  product: TSeatCoverDataDB,
  expectedSeatTypeOption: string,
  urlParam: string
): boolean {
  if (!product.seat_type_option || !product.seat_subtype || !urlParam) return false;

  // Check if the product matches the expected seat type option
  if (product.seat_type_option !== expectedSeatTypeOption) return false;

  // Simple comparison: slugify the database value and compare to URL param (which is already slugified)
  const productSeatSubtypeSlug = slugify(product.seat_subtype);

  return productSeatSubtypeSlug === urlParam;
}
